import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { MenuConfigService } from 'src/app/core/services/menu-config.service';
import { PortalFeature } from 'src/app/core/domain/member-permission.model';
import { IonicModule } from '@ionic/angular';

/**
 * Shown instead of a feature page when the caller lacks permission for it — reached via
 * featureGuard() (src/app/core/guards/auth.guard.ts), which appends `?feature=<PortalFeature>`
 * so this page can name the specific area, and via a couple of generic not-authenticated edge
 * cases (e.g. callback.page.ts) that carry no such param.
 *
 * Deliberately styled as a soft "not unlocked yet" welcome screen rather than a hard security
 * error: for the common case — a freshly invited team member whose Owner hasn't granted this
 * feature yet — nothing is wrong, they just aren't done being set up. A same-tenant colleague
 * poking at a URL they can't reach is the same message, which is fine: it's still accurate and
 * still non-alarming.
 */
@Component({
    selector: 'app-unauthorized',
    templateUrl: './unauthorized.component.html',
    styleUrls: ['./unauthorized.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonicModule, RouterLink, TranslatePipe]
})
export class UnauthorizedComponent implements OnInit {
  /** Translated title of the page the caller was denied, when reached with ?feature=. */
  featureTitle?: string;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly translate: TranslateService,
    private readonly menuConfig: MenuConfigService,
  ) {}

  ngOnInit(): void {
    const feature = this.route.snapshot.queryParamMap.get('feature') as PortalFeature | null;
    const page = feature ? this.menuConfig.portalPages.find(p => p.feature === feature) : undefined;
    if (page) this.featureTitle = this.translate.instant(page.title);
  }
}
